import React from "react";
import { BaseComponent } from ".";
import { List } from "immutable";
import * as BT from "react-bootstrap-table";
import { DefaultConnectedDispatch, DefaultConnectedState, DefaultStatePropsMap } from "./BaseComponent";
import { AppState } from "../../store";
import { BootstrapTableActions } from "../table";

export interface DefaultListViewConnectedState extends DefaultConnectedState {
    tableState?: BaseListViewTableState;
}

export const DefaultListViewStatePropsMap = (state: AppState, ownProps: OwnProps | undefined): DefaultListViewConnectedState => ({
    ...DefaultStatePropsMap(state, ownProps),
    tableState: state.router.location ? state.router.location.state : undefined,
});

type CombinedProps = DefaultListViewConnectedState & DefaultConnectedDispatch;

interface OwnProps {
}

interface OwnState {
    tablePage: number;
    tablePageSize: number;
    tableSearch: string;
    tableSearchResults?: ReadonlyArray<any>;
    tableSortField?: string;
    tableSortOrder?: BT.SortOrder;
}

export interface BaseListViewTableState {
    page: number;
    pageSize: number;
    search: string;
    searchResults?: number;
    sortField?: string;
    sortOrder?: BT.SortOrder;
}

export class BaseListViewComponent<TProps, TState> extends BaseComponent<TProps & OwnProps, TState & OwnState> {

    public table: BT.BootstrapTable | null = null;
    public tableStateInitialized = false;

    public constructor(props: any) {
        super(props);
    }

    public getInitialListViewState(props: CombinedProps) {
        const state: OwnState = {
            tablePage: 1,
            tablePageSize: props.tableState ? (props.tableState.pageSize) : 10,
            tableSearch: "",
        };
        return state;
    }

    public getTableActions(options: {
        changeLength?: boolean;
        changePage?: boolean;
        dataLength: number;
        filteredLength: number;
        initialPageSize?: number;
        lengthMenu?: number[];
        pageSize?: number;
        search?: boolean;
        searchPlaceholder?: string;
    }) {
        const props = (this.props as any) as CombinedProps;
        const state = (this.state as any) as OwnState;
        return this.table
            ? <BootstrapTableActions
                changeLength={options.changeLength}
                changePage={options.changePage}
                dataLength={options.dataLength}
                filteredLength={state.tableSearchResults
                    ? Math.min(options.filteredLength, state.tableSearchResults.length)
                    : options.filteredLength}
                page={this.state.tablePage}
                pageSize={options.pageSize || this.state.tablePageSize}
                initialPageSize={options.initialPageSize}
                initialSearchText={props.tableState ? (props.tableState.search) : ""}
                lengthMenu={options.lengthMenu}
                search={options.search}
                searchPlaceholder={options.searchPlaceholder}
                table={this.table}
                onPageChange={(tablePage) => this.handleTablePageChange(tablePage)}
                onPageSizeChange={(tablePageSize) => this.handleTablePageSizeChange(tablePageSize)}
                onSearch={(search) => this.handleTableSearch(search)}
            />
            : null;
    }

    public getTableState(): BaseListViewTableState {
        return {
            page: this.state.tablePage,
            pageSize: this.state.tablePageSize,
            search: this.state.tableSearch,
            searchResults: this.state.tableSearchResults ? this.state.tableSearchResults.length : undefined,
            sortField: this.state.tableSortField,
            sortOrder: this.state.tableSortOrder,
        };
    }

    public handleTableSearch(search: string) {
        if (this.table)
            this.table.handleSearch(search);
    }

    public handleTableSort(sortOrder: BT.SortOrder, sortField: string) {
        const tableState: BaseListViewTableState = {
            ...this.getTableState(),
            sortOrder,
            sortField,
        };
        this.saveTableState(tableState);
    }

    public handleTablePageChange(tablePage: number) {
        this.setState({ ...(this.state as any), tablePage });
        const tableState: BaseListViewTableState = {
            ...this.getTableState(),
            page: tablePage,
        };
        this.saveTableState(tableState);
    }

    public handleTablePageSizeChange(tablePageSize: number) {
        this.setState({ ...(this.state as any), tablePage: 1, tablePageSize });
        const tableState: BaseListViewTableState = {
            ...this.getTableState(),
            pageSize: tablePageSize,
        };
        this.saveTableState(tableState);
    }

    public async onTableSearch(search: string, results: ReadonlyArray<any>, callback?: (search: string, results: ReadonlyArray<any>) => void) {
        const props = (this.props as any) as CombinedProps;
        if (search !== null && search !== this.state.tableSearch) {
            await this.setStateAsync({
                ...(this.state as any),
                tableSearch: search || "",
                tableSearchResults: search === "" || search === null ? undefined : results,
            });

            // update table page if initially search available from state
            if (!this.tableStateInitialized
                    && props.tableState
                    && props.tableState.page > 1
                    && this.tablePageExists(props.tableState.page, props.tableState.pageSize, results as [])) {
                this.handleTablePageChange(props.tableState.page);
                this.tableStateInitialized = true;
            }
            else {
                this.handleTablePageChange(1);
                const tableState: BaseListViewTableState = {
                    page: 1,
                    pageSize: this.state.tablePageSize,
                    search,
                    searchResults: search === "" || search === null ? undefined : results.length,
                };
                this.saveTableState(tableState);
            }

            if (typeof callback === "function")
                await callback(search, results);
        }
    }

    public saveTableState(tableState?: BaseListViewTableState) {
        if (!tableState)
            tableState = this.getTableState();

        const props = (this.props as any) as CombinedProps;
        props.replace(window.location.pathname + window.location.search + window.location.hash, tableState);
    }

    public tablePageExists<T>(page: number, pageSize: number, data: T[] | List<T>) {
        const pages = Array.isArray(data)
            ? Math.ceil(data.length / pageSize)
            : data.size / pageSize;
        return page <= pages;
    }

    public updateTableState<T>(data: T[] | List<T>) {
        const props = (this.props as any) as CombinedProps;
        const dataSize = Array.isArray(data) ? data.length : data.size;
        if (dataSize && !this.tableStateInitialized && props.tableState) {
            // handle sort
            if (props.tableState.sortField && props.tableState.sortOrder && this.table)
                this.table.handleSort(props.tableState.sortOrder, props.tableState.sortField);

            if (props.tableState.search && this.table)
                // then handle search (paging is handled after search)
                this.table.handleSearch(props.tableState.search);
            else if (props.tableState.page > 1 && this.tablePageExists(props.tableState.page, props.tableState.pageSize, data)) {
                // or page number if no search
                this.handleTablePageChange(props.tableState.page);
                this.tableStateInitialized = true;
            }
            else
                this.tableStateInitialized = true;
        }
    }
}



// WEBPACK FOOTER //
// ./src/components/base/BaseListViewComponent.tsx